import { Box, Button, Divider, Grid, Paper, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelConfirmDialog } from '../../../components';
import {
  createIntegerInputHandler,
  createIntegerKeyDownHandler,
} from '../../../utils/number-input-handlers';
import { validateNumberFormat } from '../../../utils/number-validation';

// Types for Due Dates Settings
export interface DueDatesSettingsData {
  leadTime: number;
  deliveryWindow: number;
  maxLinealFeet: number;
}

export interface DueDatesSettingsProps {
  initialData?: DueDatesSettingsData;
  onSave: (data: DueDatesSettingsData) => void;
  isSaving?: boolean;
  readOnly?: boolean;
}

const DueDatesSettings: React.FC<DueDatesSettingsProps> = ({
  initialData,
  onSave,
  isSaving = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State
  const [leadTime, setLeadTime] = useState<number | null | undefined>(
    initialData?.leadTime ?? null
  );
  const [deliveryWindow, setDeliveryWindow] = useState<number | null | undefined>(
    initialData?.deliveryWindow ?? null
  );
  const [maxLinealFeet, setMaxLinealFeet] = useState<number | null | undefined>(
    initialData?.maxLinealFeet ?? null
  );
  const [leadTimeError, setLeadTimeError] = useState<string>('');
  const [deliveryWindowError, setDeliveryWindowError] = useState<string>('');
  const [maxLinealFeetError, setMaxLinealFeetError] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);

  // Sync state from initialData only on initial mount or when switching manufacturers
  const prevInitialDataRef = React.useRef(initialData);
  useEffect(() => {
    // Only update if this is the first load (prevRef is null/undefined)
    // OR if the manufacturer/component changed (completely different data)
    // OR if initialData changed from a value to undefined/null (manufacturer with no settings)
    const isInitialLoad = !prevInitialDataRef.current;
    const dataChanged =
      prevInitialDataRef.current &&
      initialData &&
      (prevInitialDataRef.current.leadTime !== initialData.leadTime ||
        prevInitialDataRef.current.deliveryWindow !== initialData.deliveryWindow ||
        prevInitialDataRef.current.maxLinealFeet !== initialData.maxLinealFeet);
    const dataCleared = prevInitialDataRef.current && !initialData;

    if (initialData && (isInitialLoad || dataChanged)) {
      setLeadTime(initialData.leadTime);
      setDeliveryWindow(initialData.deliveryWindow);
      setMaxLinealFeet(initialData.maxLinealFeet);
      setHasUnsavedChanges(false);
      prevInitialDataRef.current = initialData;
    } else if (dataCleared) {
      // Reset to default values when switching to a manufacturer with no saved settings
      setLeadTime(0);
      setDeliveryWindow(0);
      setMaxLinealFeet(0);
      setLeadTimeError('');
      setDeliveryWindowError('');
      setMaxLinealFeetError('');
      setHasUnsavedChanges(false);
      prevInitialDataRef.current = initialData;
    }
  }, [initialData]);

  // Handlers
  const handleNumberFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    _fieldName: string
  ) => {
    const input = event.target as HTMLInputElement;
    if (input.value === '0' || input.value === '0.00') {
      setTimeout(() => {
        input.select();
      }, 0);
    }
  };

  // Use shared handlers for number input validation
  const handleKeyDown = createIntegerKeyDownHandler();

  // Additional safeguard: filter invalid characters on input (catches paste, etc.)
  const handleLeadTimeInput = createIntegerInputHandler<string>((value: string) => {
    handleLeadTimeChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
  });

  const handleDeliveryWindowInput = createIntegerInputHandler<string>((value: string) => {
    handleDeliveryWindowChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
  });

  const handleMaxLinealFeetInput = createIntegerInputHandler<string>((value: string) => {
    handleMaxLinealFeetChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
  });

  const handleNumberBlurWithField = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: (value: number | null | undefined) => void,
    _fieldName: string
  ) => {
    handleNumberBlur(event, setter);
  };

  const handleNumberBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    setter: (value: number | null | undefined) => void
  ) => {
    const value = (event.target as HTMLInputElement).value;
    if (value === '') {
      setter(null);
    } else {
      const numValue = parseInt(value) || 0;
      setter(numValue);
    }
  };

  const handleLeadTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setHasUnsavedChanges(true);

    // Allow empty string during editing
    if (inputValue === '') {
      setLeadTime(null);
      setLeadTimeError('');
      return;
    }

    // Validate format (leading zeros, decimals for integer fields)
    // Note: Scientific notation is prevented by onKeyDown/onInput handlers
    const formatValidation = validateNumberFormat(inputValue, false);
    if (!formatValidation.isValid) {
      setLeadTimeError(formatValidation.error || 'Invalid number format');
      return;
    }

    // Parse the number
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed)) {
      setLeadTimeError('Please enter a valid number');
      return;
    }

    const value = parsed;
    setLeadTime(value);

    // Validation: Required, whole numbers, min 0, max 30
    if (value < 0 || value > 30) {
      setLeadTimeError('Lead time is required and must be between 0 and 30');
    } else {
      setLeadTimeError('');
    }
  };

  const handleDeliveryWindowChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setHasUnsavedChanges(true);

    // Allow empty string during editing
    if (inputValue === '') {
      setDeliveryWindow(null);
      setDeliveryWindowError('');
      return;
    }

    // Validate format (leading zeros, decimals for integer fields)
    // Note: Scientific notation is prevented by onKeyDown/onInput handlers
    const formatValidation = validateNumberFormat(inputValue, false);
    if (!formatValidation.isValid) {
      setDeliveryWindowError(formatValidation.error || 'Invalid number format');
      return;
    }

    // Parse the number
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed)) {
      setDeliveryWindowError('Please enter a valid number');
      return;
    }

    const value = parsed;
    setDeliveryWindow(value);

    // Validation: Required, whole numbers, min 0, max 30
    if (value < 0 || value > 30) {
      setDeliveryWindowError('Delivery window is required and must be between 0 and 30');
    } else {
      setDeliveryWindowError('');
    }
  };

  const handleMaxLinealFeetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setHasUnsavedChanges(true);

    // Allow empty string during editing
    if (inputValue === '') {
      setMaxLinealFeet(null);
      setMaxLinealFeetError('');
      return;
    }

    // Validate format (leading zeros, decimals for integer fields)
    // Note: Scientific notation is prevented by onKeyDown/onInput handlers
    const formatValidation = validateNumberFormat(inputValue, false);
    if (!formatValidation.isValid) {
      setMaxLinealFeetError(formatValidation.error || 'Invalid number format');
      return;
    }

    // Parse the number
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed)) {
      setMaxLinealFeetError('Please enter a valid number');
      return;
    }

    const value = parsed;
    setMaxLinealFeet(value);

    // Validation: Required, whole numbers, min 1, max 99,000
    if (!value || value < 1) {
      setMaxLinealFeetError('Max lineal feet is required and must be at least 1');
    } else if (value > 99000) {
      setMaxLinealFeetError('Max lineal feet must not exceed 99,000');
    } else {
      setMaxLinealFeetError('');
    }
  };

  const handleSave = () => {
    // Validate all fields before saving
    const isLeadTimeValid =
      leadTime !== null && leadTime !== undefined && leadTime >= 0 && leadTime <= 30;
    const isDeliveryWindowValid =
      deliveryWindow !== null &&
      deliveryWindow !== undefined &&
      deliveryWindow >= 0 &&
      deliveryWindow <= 30;
    const isMaxLinealFeetValid =
      maxLinealFeet !== null &&
      maxLinealFeet !== undefined &&
      maxLinealFeet >= 1 &&
      maxLinealFeet <= 99000;

    if (!isLeadTimeValid) {
      if (leadTime === null || leadTime === undefined) {
        setLeadTimeError('Lead time is required');
      } else if (leadTime < 0) {
        setLeadTimeError('Lead time must be at least 0');
      } else {
        setLeadTimeError('Lead time must not exceed 30');
      }
    }

    if (!isDeliveryWindowValid) {
      if (deliveryWindow === null || deliveryWindow === undefined) {
        setDeliveryWindowError('Delivery window is required');
      } else if (deliveryWindow < 0) {
        setDeliveryWindowError('Delivery window must be at least 0');
      } else {
        setDeliveryWindowError('Delivery window must not exceed 30');
      }
    }

    if (!isMaxLinealFeetValid) {
      if (maxLinealFeet === null || maxLinealFeet === undefined) {
        setMaxLinealFeetError('Max lineal feet is required');
      } else if (maxLinealFeet < 1) {
        setMaxLinealFeetError('Max lineal feet is required and must be at least 1');
      } else {
        setMaxLinealFeetError('Max lineal feet must not exceed 99,000');
      }
    }

    if (!isLeadTimeValid || !isDeliveryWindowValid || !isMaxLinealFeetValid) {
      return;
    }

    const saveData: DueDatesSettingsData = {
      leadTime: leadTime ?? 0,
      deliveryWindow: deliveryWindow ?? 0,
      maxLinealFeet: maxLinealFeet ?? 0,
    };

    onSave(saveData);
    setHasUnsavedChanges(false);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      handleCancelConfirm();
    }
  };

  const handleCancelConfirm = () => {
    setLeadTime(initialData?.leadTime ?? null);
    setDeliveryWindow(initialData?.deliveryWindow ?? null);
    setMaxLinealFeet(initialData?.maxLinealFeet ?? null);
    setLeadTimeError('');
    setDeliveryWindowError('');
    setMaxLinealFeetError('');
    setHasUnsavedChanges(false);
    setShowCancelDialog(false);
    navigate('/');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Due Date Criteria
        </Typography>

        <Grid container spacing={3}>
          {/* Lead Time Row */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Lead Time"
                  type="number"
                  variant="outlined"
                  value={leadTime === null || leadTime === undefined ? '' : leadTime}
                  onChange={handleLeadTimeChange}
                  onInput={handleLeadTimeInput}
                  onKeyDown={handleKeyDown}
                  onFocus={e => handleNumberFocus(e, 'leadTime')}
                  onBlur={e => handleNumberBlurWithField(e, setLeadTime, 'leadTime')}
                  inputProps={{ min: 0, max: 30, step: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!leadTimeError}
                  helperText={leadTimeError}
                  required
                  disabled={readOnly}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120, mr: 2 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  (Business Days)
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Lead time in the number of business days is an important factor in calculating the
                  due date for a truss manufacturing order. It represents the time required to
                  process an order from the moment it is placed until the manufacturing begins or
                  the product is shipped.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Delivery Window Row */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Delivery Window"
                  type="number"
                  variant="outlined"
                  value={
                    deliveryWindow === null || deliveryWindow === undefined ? '' : deliveryWindow
                  }
                  onChange={handleDeliveryWindowChange}
                  onInput={handleDeliveryWindowInput}
                  onKeyDown={handleKeyDown}
                  onFocus={e => handleNumberFocus(e, 'deliveryWindow')}
                  onBlur={e => handleNumberBlurWithField(e, setDeliveryWindow, 'deliveryWindow')}
                  inputProps={{ min: 0, max: 30, step: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!deliveryWindowError}
                  helperText={deliveryWindowError}
                  required
                  disabled={readOnly}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120, mr: 2 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  (Business Days)
                </Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Delivery Window is a target timeframe for delivering steel products to customers.
                  It represents the preferred period during which the manufacturer aims to complete
                  and deliver all orders. Unlike lead time which focuses on preparation before
                  manufacturing, the delivery window encompasses the entire process from order
                  placement to final delivery.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Max Lineal Feet Row */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>
                <TextField
                  fullWidth
                  label="Max Lineal Feet for Due Date"
                  type="number"
                  variant="outlined"
                  value={maxLinealFeet === null || maxLinealFeet === undefined ? '' : maxLinealFeet}
                  onChange={handleMaxLinealFeetChange}
                  onInput={handleMaxLinealFeetInput}
                  onKeyDown={handleKeyDown}
                  onFocus={e => handleNumberFocus(e, 'maxLinealFeet')}
                  onBlur={e => handleNumberBlurWithField(e, setMaxLinealFeet, 'maxLinealFeet')}
                  inputProps={{ min: 1, max: 99000, step: 1 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!maxLinealFeetError}
                  helperText={maxLinealFeetError}
                  required
                  disabled={readOnly}
                />
              </Box>
              <Box sx={{ minWidth: 120, mr: 2 }} />
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  In the context of a steel truss manufacturer, the "Max Lineal Feet for Due Date"
                  refers to the maximum amount of linear feet of steel trusses that can be produced
                  by the manufacturing team within a given time frame, typically on a daily basis.
                  This metric is used to calculate the due date for a manufacturing order based on
                  the total length of trusses required in the order.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={handleCancel} disabled={isSaving || readOnly}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !hasUnsavedChanges ||
            !!leadTimeError ||
            !!deliveryWindowError ||
            !!maxLinealFeetError ||
            isSaving ||
            readOnly
          }
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <CancelConfirmDialog
        open={showCancelDialog}
        onConfirm={handleCancelConfirm}
        onDismiss={() => setShowCancelDialog(false)}
        dialogTitle="Cancel Changes?"
        dialogContent="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
        continuePrompt="Stay"
        cancelPrompt="Leave"
      />
    </Box>
  );
};

export default DueDatesSettings;
